import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ProviderChip } from "@/components/FuelCards";
import { ArrowLeft, Euro, Droplet, Fuel, Route, Gauge, CalendarDays, CalendarRange, UserCheck } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, Plate, DemoTag, ExportBtn, Chip, useFetch } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TxTable, TxSummary } from "@/pages/commerciaux/Mooncard";
import { useAuth } from "@/context/AuthContext";
import { fEur, fKm, fNum, fDate, fMonth } from "@/lib/api";

const LINE_COLORS = ["#0E2D43", "#F49619", "#2F80ED", "#27AE60", "#9B51E0"];

function OdometerChart({ points }) {
  const plates = [...new Set(points.map((p) => p.plate))];
  const rows = Object.values(points.reduce((acc, p) => {
    acc[p.date] = { ...(acc[p.date] || { date: p.date }), [p.plate]: p.mileage };
    return acc;
  }, {}));
  return (
    <Panel title="Évolution du kilométrage" testid="chart-odometer">
      {!rows.length ? <Empty text="Aucun kilométrage" /> : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tickFormatter={fDate} tick={{ fontSize: 11 }} />
              <YAxis domain={["dataMin", "dataMax"]} tickFormatter={(x) => fNum(x)} tick={{ fontSize: 11 }} width={70} />
              <Tooltip labelFormatter={fDate} formatter={(v, name) => [fKm(v), name]} />
              {plates.map((pl, i) => <Line key={pl} type="monotone" dataKey={pl} name={pl} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {plates.length > 1 && <p className="text-xs text-slate-500 mt-2">Une courbe par véhicule : chaque compteur repart de son propre kilométrage.</p>}
    </Panel>
  );
}
import { VehicleMaintenance } from "@/pages/livraison/VehicleFiche";
import { ReadingsHistory } from "@/components/ReadingsHistory";
import { LicencePanel } from "@/components/Documents";
import { CongesAdmin, UsageChip, PersoTypesSub } from "@/components/Conges";
import { ComSpaceAdmin } from "@/components/ComSpace";

const grid = <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />;

function Chart({ title, data, x, y, fmtX, fmtY, type = "line", color = "#F49619", testid, domain }) {
  return (
    <Panel title={title} testid={testid}>
      {data.length < 2 ? <Empty text="Pas assez de données" /> : (
        <div className="h-56">
          <ResponsiveContainer>
            {type === "bar" ? (
              <BarChart data={data}>{grid}<XAxis dataKey={x} tickFormatter={fmtX} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip labelFormatter={fmtX} formatter={fmtY} /><Bar dataKey={y} fill={color} radius={[4, 4, 0, 0]} /></BarChart>
            ) : (
              <LineChart data={data}>{grid}<XAxis dataKey={x} tickFormatter={fmtX} tick={{ fontSize: 11 }} minTickGap={30} />
                <YAxis tick={{ fontSize: 11 }} width={70} domain={domain || ["auto", "auto"]} /><Tooltip labelFormatter={fmtX} formatter={fmtY} />
                <Line dataKey={y} stroke={color} strokeWidth={2.5} dot={false} /></LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export default function CommercialFiche() {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = user.role === "admin";
  const { data, loading, error } = useFetch(`/commerciaux/${id}/fiche`);
  const [prov, setProv] = useState("");
  if (loading) return <Loading />;
  if (!data) return <Empty text={error || "Fiche indisponible"} />;
  const { stats: s, user: u, vehicle: v } = data;
  return (
    <div data-testid="commercial-fiche">
      {admin && <Link to="/commerciaux/equipe" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2D43] mb-4" data-testid="back-to-commerciaux"><ArrowLeft className="w-4 h-4" /> Commerciaux</Link>}
      <PageHeader overline="Fiche commercial" title={u.name}>
        <div className="flex items-center gap-2">{v && <Plate>{v.plate}</Plate>}{v && <span className="text-sm text-slate-500">{v.brand} {v.model} · {v.engine}</span>}<DemoTag show={u.is_demo} /></div>
        {v?.mileage ? <div className="text-xs text-slate-500" data-testid="fiche-last-mileage">Dernier kilométrage connu : <b className="text-[#0E2D43]">{fKm(v.mileage)}</b>{v.mileage_source_label ? ` · Source : ${v.mileage_source_label}` : ""}{v.mileage_date ? ` · le ${fDate(v.mileage_date)}` : ""}</div> : null}
        {admin && <ExportBtn path={`/export/pdf/commercial/${id}`} filename={`fiche-${u.name}.pdf`} label="Fiche PDF" pdf testid="export-fiche-pdf" />}
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="fiche-spend" label="Dépenses carburant totales" value={fEur(s.spend, 0)} sub={`Mooncard ${fEur(s.spend_mooncard, 0)} · Fleet Pro ${fEur(s.spend_fleetpro, 0)}`} icon={Euro} tone="navy" />
        <Kpi testid="fiche-liters" label="Litres consommés" value={`${fNum(s.liters, 0)} L`} icon={Droplet} delay={1} />
        <Kpi testid="fiche-ppl" label="Prix moyen pondéré" value={s.ppl ? `${fNum(s.ppl, 3)} €/L` : "—"} icon={Fuel} delay={2} />
        <Kpi testid="fiche-km" label="Km parcourus" value={fKm(s.km)} sub={`dont ${fKm(s.perso_km)} perso — ${PersoTypesSub({ t: s.perso_types })}`} icon={Route} delay={3} />
        <Kpi testid="fiche-l100" label="Consommation estimée" value={s.l100 ? `${fNum(s.l100, 2)} L/100` : "—"} sub={`Coût : ${fEur(s.cost100)} / 100 km`} icon={Gauge} tone="navy" delay={4} />
        <Kpi testid="fiche-spend-month" label="Dépenses du mois" value={fEur(s.spend_month)} sub={`Année : ${fEur(s.spend_year, 0)}`} icon={CalendarDays} delay={5} />
        <Kpi testid="fiche-km-year" label="Km annuel / estimé" value={s.km_year_incomplete && !s.km_year ? "Données incomplètes" : fKm(s.km_year)} sub={s.km_year_incomplete ? `Données incomplètes sur une période · ${(s.km_year_segments || []).length} véhicule(s)` : `Projection : ${fKm(s.km_year_est)}`} icon={CalendarRange} delay={6} />
        <Kpi testid="fiche-spend-year-est" label="Dépense annuelle estimée" value={fEur(s.spend_year_est, 0)} sub={`Km perso moy. : ${fKm(s.perso_week_avg)} / sem.`} icon={UserCheck} delay={7} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Chart testid="chart-cumulative" title="Dépenses cumulées dans le temps" data={data.spend_series} x="date" y="cumulative" fmtX={fDate} fmtY={(x) => fEur(x)} color="#0E2D43" />
        <Panel title="Dépenses mensuelles par fournisseur" testid="chart-monthly">
          {!data.monthly.length ? <Empty /> : (
            <div className="h-56"><ResponsiveContainer><BarChart data={data.monthly}>{grid}<XAxis dataKey="month" tickFormatter={fMonth} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={60} />
              <Tooltip labelFormatter={fMonth} formatter={(x) => fEur(x)} /><Bar dataKey="mooncard" name="Mooncard" stackId="s" fill="#F49619" /><Bar dataKey="fleetpro" name="Fleet Pro" stackId="s" fill="#2F80ED" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          )}
        </Panel>
        <Chart testid="chart-ppl" title="Évolution du prix au litre" data={data.spend_series.filter((p) => p.ppl)} x="date" y="ppl" fmtX={fDate} fmtY={(x) => `${fNum(x, 3)} €/L`} />
        <OdometerChart points={data.mileage_series} />
      </div>
      <Tabs defaultValue="tx">
        <TabsList className="flex-wrap h-auto mb-3">
          <TabsTrigger value="tx" data-testid="tab-fiche-tx">Carburant (chronologique)</TabsTrigger>
          <TabsTrigger value="weeks" data-testid="tab-fiche-weeks">Km personnels</TabsTrigger>
          {admin && <TabsTrigger value="conges" data-testid="tab-fiche-conges">Congés avec véhicule</TabsTrigger>}
          <TabsTrigger value="readings" data-testid="tab-fiche-readings">Relevés & photos</TabsTrigger>
          {admin && <TabsTrigger value="licence" data-testid="tab-fiche-licence">Permis de conduire</TabsTrigger>}
          <TabsTrigger value="vehicles" data-testid="tab-fiche-vehicles">Historique véhicules</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-fiche-maintenance">Entretiens</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-fiche-alerts">Alertes ({data.alerts.length})</TabsTrigger>
        </TabsList>
        <Panel flush>
          <TabsContent value="tx" className="m-0">
            <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-100" data-testid="fiche-provider-filter">
              <span className="text-xs font-semibold text-slate-500">Fournisseur de carte :</span>
              {[["", "Tous"], ["mooncard", "Mooncard"], ["fleetpro", "Fleet Pro"]].map(([v, l]) => (
                <button key={v || "all"} onClick={() => setProv(v)} data-testid={`fiche-provider-${v || "all"}`} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${prov === v ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0E2D43]"}`}>{l}</button>
              ))}
            </div>
            <div className="px-3 pt-3"><TxSummary rows={data.transactions.filter((t) => !prov || (t.provider === "fleetpro") === (prov === "fleetpro"))} testid="fiche-fuel-summary" /></div>
            <TxTable rows={data.transactions.filter((t) => !prov || (t.provider === "fleetpro") === (prov === "fleetpro"))} showUser={false} />
          </TabsContent>
          <TabsContent value="weeks" className="m-0">
            {!data.weeks.length ? <Empty /> : (
              <table className="tbl w-full"><thead><tr><th>Période</th><th>Type</th><th>Début</th><th>Fin</th><th className="text-right">Km perso</th></tr></thead>
                <tbody>{data.weeks.map((w) => (
                  <tr key={`${w.key_date}-${w.conges_id || w.hist_id || "w"}`}><td className="font-mono text-xs">{w.week_label}</td><td><UsageChip w={w} /></td>
                    <td>{w.source === "historique" ? <span className="text-xs text-violet-700 font-semibold" data-testid="fiche-hist-week">Historique importé / saisi manuellement ({w.origin})</span> : w.friday_date ? `${fDate(w.friday_date)} · ${fNum(w.friday_km)}` : <span className="text-amber-600">manquant</span>}</td>
                    <td>{w.source === "historique" ? "—" : w.monday_date ? `${fDate(w.monday_date)} · ${fNum(w.monday_km)}` : <span className="text-amber-600">{w.usage_type === "conges" ? "en cours" : "manquant"}</span>}</td>
                    <td className="text-right font-bold" title={w.excluded_reason || ""}>{w.perso_km === null ? (w.excluded ? <span className="text-xs font-normal text-sky-700">inclus dans les congés</span> : "—") : fNum(w.perso_km)}</td></tr>
                ))}</tbody></table>
            )}
          </TabsContent>
          {admin && <TabsContent value="conges" className="m-0"><CongesAdmin userId={id} /></TabsContent>}
          <TabsContent value="readings" className="m-0"><ReadingsHistory params={{ user_id: id }} /></TabsContent>
          {admin && <TabsContent value="licence" className="m-0"><LicencePanel ownerType="user" ownerId={id} /></TabsContent>}
          <TabsContent value="maintenance" className="m-0">{v ? <VehicleMaintenance vehicleId={v.id} /> : <Empty text="Aucun véhicule affecté" />}</TabsContent>
          <TabsContent value="vehicles" className="m-0">
            {!data.assignments.length ? <Empty /> : (
              <table className="tbl w-full"><thead><tr><th>Véhicule</th><th>Modèle</th><th>Début</th><th>Fin</th><th>Origine</th></tr></thead>
                <tbody>{data.assignments.map((a) => (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}><td><Plate>{a.plate}</Plate></td><td>{a.model}</td><td>{fDate(a.start_date)}</td><td>{a.end_date ? fDate(a.end_date) : "En cours"}</td><td className="text-xs text-slate-500">{a.source_label}</td></tr>
                ))}</tbody></table>
            )}
          </TabsContent>
          <TabsContent value="alerts" className="m-0">
            {!data.alerts.length ? <Empty text="Aucune alerte" /> : (
              <ul className="divide-y divide-slate-100">{data.alerts.map((a) => (
                <li key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2"><Chip cls={a.severity === "danger" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{a.label}</Chip><span className="text-slate-500 text-xs">{a.detail}</span></span>
                  <span className="text-xs text-slate-400">{fDate(a.date)}</span>
                </li>
              ))}</ul>
            )}
          </TabsContent>
        </Panel>
      </Tabs>
      {admin && <ComSpaceAdmin userId={id} />}
    </div>
  );
}
